import { addDays, addMinutes } from '../../../library/dateTimeHelper';
import { EmptySlotResponse } from '../../../library/models/appointment';
import ResourcesIds from '../../../resources/ResourcesIds';

/**
 * Everything the week strip needs to work out, done on strings.
 *
 * Not one `Date` in this file, and that is the point. `startTime` arrives as
 * `2026-03-10T09:00:00`, a wall clock with no zone, and the two things ever asked of it --
 * which day it falls on and which hour it starts at -- are the first ten and the eleventh
 * and twelfth characters. Parsing it would re-express it in the viewer's offset and move a
 * nine o'clock slot to eight for anybody an hour west.
 *
 * A lexicographic comparison of two zero-padded wall clocks is a chronological one, which
 * is what lets `isBookableFrom` compare the cutoff to a slot without arithmetic.
 */

/**
 * How far ahead a patient has to book: at 08:00 the first slot on offer is 12:00.
 *
 * The API's twin is `AppointmentsService.PATIENT_BOOKING_NOTICE`, which refuses a booking
 * inside it and leaves such windows out of a patient's `empty-slots` answer. Change the two
 * together -- this copy exists so the strip stops offering an hour the moment it goes stale,
 * rather than whenever something next refetches.
 */
export const PATIENT_BOOKING_NOTICE_MINUTES = 240;

/** The three parts of a day a patient actually chooses between. */
export type SlotPeriod = 'MORNING' | 'AFTERNOON' | 'EVENING';

/** Noon and five, which is where a clinic day visibly changes character. */
const AFTERNOON_FROM = 12;
const EVENING_FROM = 17;

export const PERIOD_LABELS: Record<SlotPeriod, string> = {
    MORNING: ResourcesIds.BOOK_APPOINTMENT_PERIOD_MORNING,
    AFTERNOON: ResourcesIds.BOOK_APPOINTMENT_PERIOD_AFTERNOON,
    EVENING: ResourcesIds.BOOK_APPOINTMENT_PERIOD_EVENING,
};

/** Drawn in this order, and an empty one is left out rather than shown empty. */
export const PERIOD_ORDER: SlotPeriod[] = ['MORNING', 'AFTERNOON', 'EVENING'];

/** The seven dates a strip anchored on `weekStart` draws. */
export const weekDays = (weekStart: string): string[] =>
    Array.from({ length: 7 }, (_unused, offset: number) => addDays(weekStart, offset));

/** The ISO date a slot starts on. */
export const dayOf = (startTime: string): string => startTime.slice(0, 10);

/** The `HH:mm` a slot starts at -- what a chip is labelled with. */
export const clockOf = (startTime: string): string => startTime.slice(11, 16);

/** The hour, as a number, read off the string rather than parsed out of a Date. */
const hourOf = (startTime: string): number => Number(startTime.slice(11, 13));

export const periodOf = (startTime: string): SlotPeriod => {
    const hour: number = hourOf(startTime);

    if (hour < AFTERNOON_FROM) {
        return 'MORNING';
    }

    return hour < EVENING_FROM ? 'AFTERNOON' : 'EVENING';
};

/**
 * The first `HH:mm` a patient may book, as of `nowValue`.
 *
 * `nowValue` is `toDateTimeInputValue(new Date())` -- the viewer's own clock in the same
 * shape the slot carries, whole minutes and no zone. The notice is added in wall-clock by
 * `addMinutes`, so a night with a daylight-saving change in it moves nothing.
 */
export const earliestBookable = (nowValue: string): string =>
    addMinutes(nowValue, PATIENT_BOOKING_NOTICE_MINUTES).slice(0, 16);

/**
 * Whether a slot starts at or after the cutoff -- a comparison between two wall clocks
 * rather than between a wall clock and an instant.
 *
 * At, not only after: the API books the slot sitting exactly on its cutoff, and counts in
 * whole minutes as this does, so 12:00 is offered at 08:00 and still at 08:00:40.
 *
 * The API filters these windows too. Doing it here as well is deliberate: the two repos
 * deploy separately, and a booking page left open across lunch would otherwise go on
 * offering an hour that has gone stale until something refetched it.
 */
export const isBookableFrom = (startTime: string, earliestValue: string): boolean =>
    startTime.slice(0, 16) >= earliestValue;

/** Slots bucketed by the day they fall on, those before the cutoff already dropped. */
export const slotsByDay = (
    slots: EmptySlotResponse[],
    earliestValue: string
): Record<string, EmptySlotResponse[]> => {
    const byDay: Record<string, EmptySlotResponse[]> = {};

    slots
        .filter((slot: EmptySlotResponse) => isBookableFrom(slot.startTime, earliestValue))
        .forEach((slot: EmptySlotResponse) => {
            const day: string = dayOf(slot.startTime);

            byDay[day] = [...(byDay[day] ?? []), slot];
        });

    return byDay;
};

/**
 * One day's slots split into the parts of the day, in order, with empty parts left out.
 *
 * Returned as pairs rather than as a record so the caller draws them in a fixed order
 * without having to know what that order is.
 */
export const groupByPeriod = (
    slots: EmptySlotResponse[]
): [SlotPeriod, EmptySlotResponse[]][] =>
    PERIOD_ORDER
        .map((period: SlotPeriod): [SlotPeriod, EmptySlotResponse[]] => [
            period,
            slots.filter((slot: EmptySlotResponse) => periodOf(slot.startTime) === period),
        ])
        .filter(([, inPeriod]) => inPeriod.length > 0);
